import { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useSideNav } from '../context/SideNavContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import Icon from '../components/Icon';
import { ICONS } from '../lib/icons';

// Default (console) navigation, shown whenever a page hasn't set a
// service-specific menu. Mirrors the left rail of the AWS/Azure/OCI consoles.
const NAV = [
  {
    section: 'Overview',
    items: [
      { to: '/home', label: 'Console home', icon: ICONS.home, feature: 'home' },
      // Not a console area: All services is offered when a policy grants at
      // least one provider service, otherwise the page would only ever say
      // "nothing granted".
      {
        to: '/services',
        label: 'All services',
        icon: ICONS.services,
        requiresService: true,
      },
    ],
  },
  {
    section: 'Manage',
    // Resources and Activity are hidden from the nav. Their routes still exist
    // (/resources, /activity) and other pages link to them, but they are not
    // offered as destinations here.
    items: [
      // Billing reads the stored snapshot and is for everyone; Cost & Billing
      // hits the live provider API and is superadmin-only.
      { to: '/billing', label: 'Billing', icon: ICONS.billing, feature: 'billing', provider: 'any' },
      { to: '/costs', label: 'Cost & Billing', icon: ICONS.costs, adminOnly: true },
      {
        to: '/contracts',
        label: 'Contract management',
        icon: ICONS.contracts,
        feature: 'contracts',
        provider: 'any',
      },
      { to: '/prices', label: 'Price management', icon: ICONS.prices, feature: 'prices', provider: 'oci' },
    ],
  },
  {
    section: 'Administration',
    items: [
      { to: '/providers', label: 'Providers', icon: ICONS.providers, feature: 'providers' },
      { to: '/admin/qemu-servers', label: 'QEMU Servers', icon: ICONS.servers, adminOnly: true },
      { to: '/iam', label: 'IAM', icon: ICONS.iam, feature: 'iam' },
      { to: '/superadmin', label: 'Superadmin', icon: ICONS.superadmin, adminOnly: true },
    ],
  },
];

export default function Sidebar({ open }) {
  const { nav } = useSideNav();
  const { isAdmin, allows, hasServices } = useAuth();
  const { pathname, search } = useLocation();
  const [openGroups, setOpenGroups] = useState(() =>
    Object.fromEntries(NAV.map((g) => [g.section, true]))
  );
  const [providerCapabilities, setProviderCapabilities] = useState({});

  useEffect(() => {
    if (!isAdmin) return;
    const loadCapabilities = async () => {
      try {
        const caps = await api.getProviderCapabilities();
        const map = {};
        caps.forEach(cap => {
          map[cap.providerId] = cap;
        });
        setProviderCapabilities(map);
      } catch (err) {
        console.error('Failed to load provider capabilities:', err);
      }
    };
    loadCapabilities();
  }, [isAdmin]);

  const toggleGroup = (section) =>
    setOpenGroups(s => ({ ...s, [section]: !s[section] }));

  const canShowItem = (item) => {
    if (!item.provider) return true;

    const capValues = Object.values(providerCapabilities);
    if (capValues.length === 0) return false;

    if (item.provider === 'any') {
      return capValues.some(cap => cap.enabled && hasFeature(cap, item.feature));
    }

    const cap = providerCapabilities[item.provider];
    return cap && cap.enabled && hasFeature(cap, item.feature);
  };

  const hasFeature = (cap, feature) => {
    switch (feature) {
      case 'billing':
        return cap.hasBilling;
      case 'contracts':
        return cap.hasContracts;
      case 'prices':
        return cap.hasPrices;
      default:
        return false;
    }
  };

  const serviceMode = !!nav;

  // For service items, active state is exact (path + query), since "list" and
  // "create" share the same pathname and differ only by query string.
  const isServiceItemActive = (to) =>
    to.includes('?')
      ? `${pathname}${search}` === to
      : pathname === to && !search.includes('action=');

  const renderServiceLink = (item, child = false) => (
    <Link
      key={item.to}
      to={item.to}
      title={item.label}
      className={`sidebar__link ${child ? 'sidebar__link--child' : ''} ${
        isServiceItemActive(item.to) ? 'sidebar__link--active' : ''
      }`}
    >
      <Icon name={item.icon} className="sidebar__link-icon" />
      <span className="sidebar__link-label">{item.label}</span>
    </Link>
  );

  return (
    <aside className={`sidebar ${open ? 'sidebar--open' : ''}`}>
      {serviceMode ? (
        <nav className="sidebar__nav">
          {nav.search ? (
            <div className="sidebar__search">
              <input
                className="input input--sm sidebar__search-field"
                placeholder={`Search ${nav.crumb || nav.title}`}
                aria-label="Search this service"
              />
            </div>
          ) : null}
          {nav.subtitle ? (
            <div className="sidebar__svc-sub">{nav.subtitle}</div>
          ) : null}
          {(nav.items || []).map((item) => renderServiceLink(item))}
          {/* Same rule as the console groups: a heading only appears when the
              section under it has something in it. */}
          {(nav.groups || []).filter((g) => (g.items || []).length > 0).map((group) => (
            <div className="sidebar__svc-group" key={group.title}>
              {group.title ? (
                <div className="sidebar__svc-group-title">{group.title}</div>
              ) : null}
              {group.items.map((item) => (
                <div key={item.to}>
                  {renderServiceLink(item)}
                  {(item.children || []).map((c) => renderServiceLink(c, true))}
                </div>
              ))}
            </div>
          ))}
          {nav.related ? (
            <div className="sidebar__svc-group">
              <div className="sidebar__svc-group-title sidebar__svc-group-title--muted">
                Related consoles
              </div>
              {nav.related.map((r) => (
                <a
                  key={r.label}
                  className="sidebar__link sidebar__link--related"
                  href={r.href || '#'}
                  onClick={(e) => e.preventDefault()}
                >
                  <span className="sidebar__link-label">{r.label} ↗</span>
                </a>
              ))}
            </div>
          ) : null}
        </nav>
      ) : (
        <nav className="sidebar__nav">
          {NAV.map((group) => {
            const isOpen = openGroups[group.section];
            // A console area is listed only when a policy grants it; adminOnly
            // items stay superadmin-gated, and requiresService ones need at
            // least one granted provider service. Resolved before the section
            // renders, so a group whose every item is filtered out drops its
            // heading too rather than leaving a title with nothing under it.
            const items = group.items
              .filter(item => !item.adminOnly || isAdmin)
              .filter(item => !item.feature || allows(item.feature))
              .filter(item => !item.requiresService || hasServices)
              .filter(item => canShowItem(item));
            if (items.length === 0) return null;
            return (
              <div
                className={`sidebar__group ${isOpen ? '' : 'sidebar__group--collapsed'}`}
                key={group.section}
              >
                <button
                  className="sidebar__group-title"
                  onClick={() => toggleGroup(group.section)}
                  aria-expanded={isOpen}
                >
                  <Icon name={ICONS.caretDown} className="sidebar__group-caret" />
                  {group.section}
                </button>
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    title={item.label}
                    className={({ isActive }) =>
                      `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`
                    }
                  >
                    <Icon name={item.icon} className="sidebar__link-icon" />
                    <span className="sidebar__link-label">{item.label}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
      )}

      <div className="sidebar__foot">
        <div className="sidebar__foot-title">Multi-cloud console</div>
        <div className="sidebar__foot-sub">OCI</div>
      </div>
    </aside>
  );
}
